import type PlantModel from "Models/Plant";
import type VarietyModel from "Models/Variety";
import Modal from "react-modal";
import ReactDropdown from "react-dropdown";
import ModalStyle from "Assets/ModalStyle";
import { formatPlantName, getPlantDisplayName } from "Helpers/PlantName";
import SearchableReactDropdown from "./SearchableReactDropdown";
import PlantCompanionPlantsField from "./PlantCompanionPlantsField";
import UnitDropdown from "./UnitDropdown";

interface PlantModalFormProps {
  isOpen: boolean;
  statusMessage?: string;
  plant?: PlantModel;
  plants: PlantModel[];
  varieties: VarietyModel[];
  variety?: VarietyModel;
  parentPlant?: PlantModel;
  parentPlantId?: string;
  selectedPlantId?: string;
  plantName?: string;
  varietyName: string;
  showCreateVariety: boolean;
  showCreateFromExistingPlant: boolean;
  showSaveValidation: boolean;
  plantCompanionPlants: string[];
  plantPlantingInfo?: string;
  plantDaysToGerminate?: number;
  plantDaysToMaturity?: number;
  plantSeedDepth?: string;
  plantSeedDepthUnit?: string;
  plantSeedSpacing?: string;
  plantSeedSpacingUnit?: string;
  plantLightRequiredToGerminate?: boolean;
  plantTemperatureTolerance?: string;
  plantWaterRequirement?: string;
  onRequestClose: () => void;
  onCancel: () => void;
  onSave: () => void;
  onPlantNameChange: (name: string) => void;
  onVarietyNameChange: (name: string) => void;
  onCreateVariety: () => void;
  onCreateFromExistingPlant: () => void;
  onParentPlantChange: (plant: PlantModel) => void;
  onCreatePlant: () => void;
  onCompanionPlantsChange: (plantIds: string[]) => void;
  onShadePreferenceChange: (value: string) => void;
  onPlantingInfoChange: (value: string) => void;
  onDaysToGerminateChange: (value: number) => void;
  onDaysToMaturityChange: (value: number) => void;
  onSeedDepthChange: (value: string) => void;
  onSeedDepthUnitChange: (value: string) => void;
  onSeedSpacingChange: (value: string) => void;
  onSeedSpacingUnitChange: (value: string) => void;
  onLightRequiredToGerminateChange: (value: boolean) => void;
  onTemperatureToleranceChange: (value: string) => void;
  onWaterRequirementChange: (value: string) => void;
}

const PlantModalForm = ({
  isOpen,
  statusMessage,
  plant,
  plants,
  varieties,
  variety,
  parentPlant,
  parentPlantId,
  selectedPlantId,
  plantName,
  varietyName,
  showCreateVariety,
  showCreateFromExistingPlant,
  showSaveValidation,
  plantCompanionPlants,
  plantPlantingInfo,
  plantDaysToGerminate,
  plantDaysToMaturity,
  plantSeedDepth,
  plantSeedDepthUnit,
  plantSeedSpacing,
  plantSeedSpacingUnit,
  plantLightRequiredToGerminate,
  plantTemperatureTolerance,
  plantWaterRequirement,
  onRequestClose,
  onCancel,
  onSave,
  onPlantNameChange,
  onVarietyNameChange,
  onCreateVariety,
  onCreateFromExistingPlant,
  onParentPlantChange,
  onCreatePlant,
  onCompanionPlantsChange,
  onShadePreferenceChange,
  onPlantingInfoChange,
  onDaysToGerminateChange,
  onDaysToMaturityChange,
  onSeedDepthUnitChange,
  onSeedDepthChange,
  onSeedSpacingChange,
  onSeedSpacingUnitChange,
  onLightRequiredToGerminateChange,
  onTemperatureToleranceChange,
  onWaterRequirementChange,
}: PlantModalFormProps) => {
  const computedVarietyName =
    varietyName ?? variety?.name ?? "Enter Variety Name";

  const showVarietyEditInfo =
    varietyName || showCreateVariety || plant?.varietyId;

  return (
    <Modal
      style={ModalStyle.defaultStyle}
      ariaHideApp={false}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
    >
      <div className={statusMessage ? "hidden" : "flex flex-wrap m-50 p-5"}>
        <h2 className="m-auto p-5 text-2xl">
          {plant?._id ? "Edit Plant" : "Create Plant"}
        </h2>
        <div className="w-full px-5">
          <h5>Name</h5>
          {(computedVarietyName.length && parentPlant?.name) ||
          showCreateVariety ||
          plant?.varietyId ? (
            <h2 className="text-2xl py-2">
              {formatPlantName(
                parentPlant?.name ?? plantName ?? "Select Parent Plant",
                computedVarietyName,
              )}
            </h2>
          ) : (
            <input
              placeholder="Name"
              value={plantName ?? plant?.name ?? ""}
              onChange={(event) => onPlantNameChange(event.target.value)}
              className="modal-input w-[100%]"
            />
          )}
          <div className="flex gap-1">
            {!variety?._id && !varietyName && !showCreateVariety && (
              <button onClick={onCreateVariety} className="btn btn-green">
                Create Variety
              </button>
            )}
            {!parentPlantId &&
              !varietyName &&
              !variety?._id &&
              !selectedPlantId &&
              !showCreateVariety && (
                <button
                  onClick={onCreateFromExistingPlant}
                  className="btn btn-green"
                >
                  Create from existing plant
                </button>
              )}
          </div>

          {(showCreateFromExistingPlant || showVarietyEditInfo) && (
            <div>
              {!parentPlantId && !plant?.parentPlantId && showCreateVariety && (
                <div>
                  <h3 className="text-lg">Select Parent Plant</h3>
                  <p className="text-red">
                    * Creating a new variety requires selecting a parent plant.
                  </p>
                </div>
              )}
              <div>
                <SearchableReactDropdown
                  options={plants.filter(
                    (candidate) =>
                      candidate._id !== selectedPlantId &&
                      !candidate.varietyId?.length,
                  )}
                  getOptionLabel={(candidate) =>
                    getPlantDisplayName(candidate, varieties)
                  }
                  getOptionValue={(candidate) => candidate._id}
                  value={
                    parentPlantId ?? plant?.parentPlantId ?? variety?.plantId
                  }
                  controlClassName="border rounded px-5 text-lg cursor-pointer mt-[10px]"
                  placeholder="Select Plant"
                  arrowClassName="py-5"
                  menuClassName="border rounded cursor-pointer"
                  onChange={onParentPlantChange}
                />

                {showCreateVariety && !parentPlant && showSaveValidation && (
                  <p className="text-red">A parent plant is required.</p>
                )}
              </div>
              {(showCreateVariety || varietyName || variety?.name) && (
                <div>
                  <h3 className="text-lg">Variety Name</h3>
                  <input
                    placeholder="Variety Name"
                    value={computedVarietyName}
                    onChange={(event) =>
                      onVarietyNameChange(event.target.value)
                    }
                    className="modal-input w-[100%]"
                  />
                  {showCreateVariety &&
                    varietyName.trim().length < 1 &&
                    showSaveValidation && (
                      <p className="text-red">A variety name is required.</p>
                    )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="w-full px-5 md:w-1/2">
          <h5>Sun Requirement</h5>
          <ReactDropdown
            options={["Full Sun", "Part Sun", "Part Shade", "Shade"]}
            controlClassName="border rounded p-5 text-lg cursor-pointer mt-[10px]"
            menuClassName="border rounded cursor-pointer"
            arrowClassName="py-5"
            placeholder="Select Sun Requirement"
            onChange={(option) => onShadePreferenceChange(option.value)}
            value={plant?.shadePreference}
          />
        </div>
        <div className="w-full px-5 md:w-1/2">
          <h5>Planting Info</h5>
          <textarea
            placeholder="Planting Info"
            value={plantPlantingInfo ?? plant?.plantingInfo ?? ""}
            onChange={(event) => onPlantingInfoChange(event.target.value)}
            className="modal-input w-[100%]"
          />
        </div>
        <PlantCompanionPlantsField
          plants={plants}
          varieties={varieties}
          companionPlantIds={plantCompanionPlants}
          onChange={onCompanionPlantsChange}
          onCreatePlant={onCreatePlant}
        />
        <div className="w-full px-5 md:w-1/2">
          <h5>Days to Germinate</h5>
          <input
            type="number"
            min={0}
            step={1}
            placeholder="Days to Germinate"
            value={plantDaysToGerminate ?? plant?.daysToGerminate ?? ""}
            onChange={(event) =>
              onDaysToGerminateChange(Number(event.target.value))
            }
            className="modal-input w-[100%]"
          />
        </div>
        <div className="w-full px-5 md:w-1/2">
          <h5>Days to Maturity</h5>
          <input
            type="number"
            min={0}
            step={1}
            placeholder="Days to Maturity"
            value={plantDaysToMaturity ?? plant?.daysToMaturity ?? ""}
            onChange={(event) =>
              onDaysToMaturityChange(Number(event.target.value))
            }
            className="modal-input w-[100%]"
          />
        </div>
        <div className="w-full px-5 md:w-1/2">
          <h5>Seed Depth</h5>
          <div className="flex">
            <input
              type="number"
              min={0}
              step={0.01}
              placeholder="Enter Seed Depth"
              value={plantSeedDepth ?? plant?.seedDepth}
              onChange={(event) => onSeedDepthChange(event.target.value)}
              className="modal-input flex-shrink"
            />

            <div className="flex-grow flex items-center px-4">
              <UnitDropdown
                measure="length"
                onChange={(event) => onSeedDepthUnitChange(event)}
                value={plantSeedDepthUnit ?? plant?.seedDepthUnit}
                arrowClassName="px-[10px]"
              />
            </div>
          </div>
        </div>
        <div className="w-full px-5 md:w-1/2">
          <h5>Seed Spacing</h5>
          <div className="flex">
            <input
              type="number"
              min={0}
              step={0.01}
              placeholder="Enter Seed Spacing"
              value={plantSeedSpacing ?? plant?.seedSpacing}
              onChange={(event) => onSeedSpacingChange(event.target.value)}
              className="modal-input flex-shrink"
            />

            <div className="flex-grow flex items-center px-4">
              <UnitDropdown
                measure="length"
                onChange={(event) => onSeedSpacingUnitChange(event)}
                value={plantSeedSpacingUnit ?? plant?.seedSpacingUnit}
                arrowClassName="px-[10px]"
              />
            </div>
          </div>
        </div>
        <div className="w-full px-5 md:w-1/2 flex items-center">
          <h5 className="pr-10">Light required to germinate</h5>
          <input
            type="checkbox"
            checked={
              plantLightRequiredToGerminate ?? plant?.lightRequiredToGerminate
            }
            onChange={(event) =>
              onLightRequiredToGerminateChange(event.target.checked)
            }
          />
        </div>
        <div className="w-full px-5 md:w-1/2">
          <h5>Temperature Tolerance</h5>
          <ReactDropdown
            options={["Cold", "Cool", "Warm", "Hot"]}
            controlClassName="border rounded p-5 text-lg cursor-pointer mt-[10px]"
            menuClassName="border rounded cursor-pointer"
            arrowClassName="py-5"
            placeholder="Select Temperature Tolerance"
            onChange={(option) => onTemperatureToleranceChange(option.value)}
            value={plantTemperatureTolerance ?? plant?.temperatureTolerance}
          />
        </div>
        <div className="w-full px-5 md:w-1/2">
          <h5>Water Requirement</h5>
          <textarea
            placeholder="Water Requirement"
            value={plantWaterRequirement ?? plant?.waterRequirement ?? ""}
            onChange={(event) => onWaterRequirementChange(event.target.value)}
            className="modal-input w-[100%]"
          />
        </div>
        <div className="flex pt-10">
          <button onClick={onCancel} className="btn bg-gray-light mr-5">
            Cancel
          </button>
          <button onClick={onSave} className="btn btn-green">
            Save
          </button>
        </div>
      </div>
      {statusMessage && (
        <div className="m-50 p-5 text-center">{statusMessage}</div>
      )}
    </Modal>
  );
};

export default PlantModalForm;
